import type { City, CityArea } from "@hermes/api";
import { useMessage } from "@hermes/intl";
import { cssFns, icons, useColors } from "@hermes/ui";
import type { ReactNode } from "react";

import { useAppConfig } from "#internal/app/config";

const InfoBox = ({ title, subtitle }: { title: string; subtitle: string }) => {
  const colors = useColors();
  return (
    <div
      css={[
        cssFns.padding(10),
        cssFns.border({
          width: 1,
          style: "solid",
          color: colors.principal.ebblue,
          radius: 12,
        }),
        { background: colors.principal.grayline },
      ]}
    >
      <div
        css={{
          display: "flex",
          gap: 8,
        }}
      >
        <div css={[cssFns.padding(3)]}>
          <icons.InfoIcon />
        </div>
        <p
          css={[
            cssFns.typo({ weight: "semibold", level: "body-1" }),
            { marginBlockStart: 2 },
          ]}
        >
          {title}
        </p>
      </div>
      <div css={{ marginInlineStart: 30 }}>{subtitle}</div>
    </div>
  );
};

export const AdditionalListWrapper = ({
  city,
  cityArea,
  insurance,
  children,
}: {
  city: City;
  cityArea?: CityArea;
  insurance?: number;
  children?: ReactNode;
}) => {
  const { insurances } = useAppConfig();
  const message = useMessage();

  if (!insurance && !cityArea) return null;

  const filterTitle =
    insurance && cityArea
      ? message({
          id: "61a8f9d0-98a0-4941-b1fa-c0bd356a9abe",
          default: "No more doctors left in {city_area}, {city}, {insurance}",
          values: {
            city_area: cityArea.name,
            city: city.name,
            insurance: insurances.find(({ id }) => id === Number(insurance))
              ?.name,
          },
        })
      : insurance
        ? message({
            id: "42764684-131a-44b2-8906-a41a4f85a98b",
            default: "No more doctors left {name}",
            values: {
              name: insurances.find(({ id }) => id === Number(insurance))?.name,
            },
          })
        : cityArea
          ? message({
              id: "61d9fe86-940d-49c3-b88a-22f64be8febf",
              default: "No more doctors left in {city_area}, {city}",
              values: {
                city_area: cityArea.name,
                city: city.name,
              },
            })
          : "";

  const filterSubtitle = cityArea
    ? message({
        id: "c7af8b3d-b7de-44d7-a79f-014b56db9d48",
        default: "Check out other doctors in nearby areas below",
      })
    : message({
        id: "f4d6ee4f-4cc5-4d04-9dd2-87b17d907a85",
        default: "Посмотрите ниже других врачей",
      });

  return (
    <>
      <InfoBox title={filterTitle} subtitle={filterSubtitle} />
      {children}
    </>
  );
};
